/**
 * SpaNavManager manages navigation of reports as a single page application.
 * i.e user feels like new page was loaded, but in reality report was swapped without a page refresh.
 * NOTE: This currently only works for iron apps.
 */

import {getVoltTab, getLegacyTab, IronAppInfo, urlToIronAppInfo} from './index';
import {createElem} from '../../util/dom';
import {waitUntilUiReady} from '../perf-measure';
import {apiClient} from '../../util/api-client';

export type InitAppFunc = () => Promise<HTMLElement>;

export interface AppAssets {
  js: string[];
  css: string[];
}

export interface AppAssetsUrls {
  [app: string]: AppAssets;
}

export interface InitOptions {
  projectId: string;
  projectName: string;
  workspaceId: number | null;
}

const appAssetsApiUrl = `/api/app/asset-manifest/iron-apps`;

export class SpaNavManager {
  private initAppFuncsMap: Map<string, InitAppFunc> = new Map();
  private appAssetsUrlMap: Map<string, AppAssets> = new Map();
  private initOptions: InitOptions;
  private curApp: IronAppInfo;
  private initialized = false;

  /** iron apps call into this via init-app.ts when initializing */
  setInitAppFn(appName: string, initAppFunc: InitAppFunc) {
    this.initAppFuncsMap.set(appName, initAppFunc);
  }

  async initialize(initOptions: InitOptions) {
    if (this.initialized) {
      return;
    }

    this.initOptions = initOptions;
    const appAssetUrls = await this.fetchAppAssetUrls();
    this.setAppAssetsUrls(appAssetUrls);
    this.initialized = true;
  }

  setAppAssetsUrls(appsAssetUrls: AppAssetsUrls) {
    if (appsAssetUrls) {
      this.appAssetsUrlMap = new Map(Object.entries(appsAssetUrls));
    }
  }

  async fetchAppAssetUrls() {
    return await apiClient.get<AppAssetsUrls>(appAssetsApiUrl);
  }

  getAppInfoFromUrl(url: string): IronAppInfo {
    let appInfo = null;
    const appNames = new Set(this.appAssetsUrlMap.keys());
    if (appNames.size) {
      appInfo = urlToIronAppInfo[getVoltTab(url) || getLegacyTab(url, this.initOptions)];
      if (!appInfo || !appNames.has(appInfo.appName)) {
        appInfo = null;
      }
    }
    return appInfo;
  }

  getProjectIdFromUrl(pathname: string): string {
    const projectIdMatch = /^\/(?:project|report|report\/workspace)\/(\d+)\//.exec(pathname);
    return projectIdMatch && projectIdMatch[1];
  }

  isNavigable(url): boolean {
    const curApp = this.getAppInfoFromUrl(window.location.pathname);
    const newApp = this.getAppInfoFromUrl(url);
    const curProjectId = this.getProjectIdFromUrl(window.location.pathname);
    const newProjectId = this.getProjectIdFromUrl(url);
    return Boolean(curApp && newApp && newProjectId === curProjectId);
  }

  async navigate(url: string, ev = {} as Event) {
    const startTime = Date.now();

    // popstate event changes current url and we have no way of knowing what its last url was
    // so we keep account via this.curApp
    const curApp = (ev.type === `popstate` && this.curApp) || this.getAppInfoFromUrl(window.location.pathname);
    const newApp = this.getAppInfoFromUrl(url);
    this.curApp = newApp;

    if (!curApp || !newApp || curApp.appName === newApp.appName) {
      // nothing to do, let the app's built-in router handle the navigation or let the browser refresh the page
      return;
    }
    if (ev.type === `click`) {
      // don't reload the page with new url, we'll do a spa navigate
      ev.preventDefault();
    }

    const $mixpanelApp = document.querySelector(`#mixpanel-application`);
    const $widthWrapper = document.querySelector(`#width_wrapper`);
    const $messageBanner = document.body.querySelector(`mp-report-message-banner`);

    // clear global action bar attributes
    const actionBarEl = document.querySelector(`global-action-bar`);
    if (actionBarEl) {
      actionBarEl.removeAttribute(`beta-banner-parts`);
      actionBarEl.removeAttribute(`beta-banner-feedback-email`);
    }

    // innerHTML = ``  will disconnect existing top level app component
    // and browser will recursively call disconnectedCallback for the component tree
    $mixpanelApp.innerHTML = ``;
    // e.g MusicFinder / Jql Console
    document.title = `${this.initOptions.projectName} / ${newApp.title}`;
    $mixpanelApp.appendChild(createElem(`mp-spinner`, {'allow-festive': true}));

    // reset full width report class
    $widthWrapper.classList.remove(`full-width-report`);

    // reset message banner state leftover by previous apps, if banner isn't persistent
    if ($messageBanner && $messageBanner.getAttribute(`persistent`) !== `true`) {
      $messageBanner.setAttribute(`visible`, `false`);
    }

    // change the globals app_name which the apps could rely on via mpContext
    if (typeof mp !== `undefined` && mp.report && mp.report.globals) {
      mp.report.globals.app_name = newApp.appName;
    }

    // change the url without re-loading to the new app's url
    if (ev.type !== `popstate`) {
      // if we push on a popstate event, we break browser back button navigation, don't do that
      window.history.pushState(``, ``, url);
    }

    // disable old css and add new css if not already loaded
    // if new css is already there, just activate it
    const appAssetsUrlMap = this.appAssetsUrlMap;
    const $curAppCss: any = document.querySelector(`head > link[href='${appAssetsUrlMap.get(curApp.appName).css[0]}']`);
    const $newAppCss: any = document.querySelector(`head > link[href='${appAssetsUrlMap.get(newApp.appName).css[0]}']`);
    if ($curAppCss) {
      $curAppCss.disabled = true; // see https://guides.codechewing.com/js/disable-enable-stylesheet-javascript
    }
    if ($newAppCss) {
      $newAppCss.disabled = false;
    } else {
      document.head.appendChild(
        createElem(`link`, {rel: `stylesheet`, href: `${appAssetsUrlMap.get(newApp.appName).css[0]}`}),
      );
    }

    // add new js script if not already loaded
    // otherwise re-initialize existing app via it's initAppFunc
    if (!this.initAppFuncsMap.has(newApp.appName)) {
      document.body.appendChild(
        createElem(`script`, {type: `text/javascript`, src: `${appAssetsUrlMap.get(newApp.appName).js[0]}`}),
      );
    } else {
      const initAppFunc = this.initAppFuncsMap.get(newApp.appName);
      const appElem = await initAppFunc();
      $mixpanelApp.innerHTML = ``;
      $mixpanelApp.appendChild(appElem);
    }

    // wait for report shell to load and track report loaded events
    await waitUntilUiReady();
    const elapsedMs = Date.now() - startTime;
    this._trackReportLoaded(newApp.appName, elapsedMs);
  }

  /**
   * track 'Viewed Report' and 'Report Loaded' events with 'is_spa_nav' flag so we can pretend a pageload occured
   */
  private _trackReportLoaded(report: string, elapsedMs: number) {
    const projectId = this.initOptions.projectId;
    const commonTrackProps = {
      project_id: projectId,
      is_spa_nav: true,
      tab: report,
    };

    mixpanel.track(`Viewed report`, commonTrackProps);

    mixpanel.track(`Report Loaded`, {
      ...commonTrackProps,
      page_time_to_ui_ready_ms: elapsedMs,
    });

    mixpanel.infra_metrics.track(`report-loaded`, {
      ...commonTrackProps,
      distinct_id: projectId,
      page_time_to_ui_ready_ms: elapsedMs,
    });
  }
}

// window singleton
export const spaNavManager: SpaNavManager = (window[`__spaNavManager`] =
  window[`__spaNavManager`] || new SpaNavManager());
